/**
 * Plugin that converts standard Lexical list nodes to our custom list nodes.
 * 
 * When Lexical's ListPlugin creates lists via commands (INSERT_UNORDERED_LIST_COMMAND, etc.),
 * it creates standard ListNode and ListItemNode. This plugin transforms them to our
 * CustomListNode and CustomListItemNode which have blockId support for AST compatibility.
 */

import { ListItemNode, ListNode } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { generateBlockId } from '@shared/ast/idGenerator'
import { useEffect } from 'react'

import { $createCustomListItemNode } from '../nodes/CustomListItemNode'
import { $createCustomListNode } from '../nodes/CustomListNode'

export function ListNodeReplacementPlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    // Transform standard ListNode to CustomListNode
    const removeListTransform = editor.registerNodeTransform(ListNode, (node) => {
      // Skip if it's already our custom node (use type string for reliable check)
      if (node.getType() === 'custom-list') {
        return
      }

      const listType = node.getListType()
      const ordered = listType === 'number'
      const start = node.getStart()
      const children = node.getChildren()
      const direction = node.getDirection()

      // Create custom node
      const customNode = $createCustomListNode(generateBlockId(), ordered, start)
      if (direction) {
        customNode.setDirection(direction)
      }

      // Move children to new node
      for (const child of children) {
        customNode.append(child)
      }

      // Replace in tree
      node.replace(customNode)
    })

    // Transform standard ListItemNode to CustomListItemNode
    const removeListItemTransform = editor.registerNodeTransform(ListItemNode, (node) => {
      // Skip if it's already our custom node (use type string for reliable check)
      if (node.getType() === 'custom-listitem') {
        return
      }

      const value = node.getValue()
      const checked = node.getChecked()
      const indent = node.getIndent()
      const children = node.getChildren()
      const direction = node.getDirection()
      const format = node.getFormatType()

      // Create custom node
      const customNode = $createCustomListItemNode(generateBlockId())
      customNode.setValue(value)
      if (checked !== undefined) {
        customNode.setChecked(checked)
      }
      customNode.setIndent(indent)
      if (direction) {
        customNode.setDirection(direction)
      }
      customNode.setFormat(format)

      // Move children to new node
      for (const child of children) {
        customNode.append(child)
      }

      // Replace in tree
      node.replace(customNode)
    })

    return () => {
      removeListTransform()
      removeListItemTransform()
    }
  }, [editor])

  return null
}

